<script lang="ts" setup>
import DateTimePicker from "@/components/pickers/DateTimePicker.vue";
import TruckStatusSelect from "@/components/statuses/TruckStatusSelect.vue";
import LocationPicker from "@/components/pickers/LocationPicker.vue";
import type { ChangeTruckStatusData, LocationTruckDto, TruckDto } from "@/api/trucks/dto/truck.dto";
import { computed, onMounted, ref, watch } from "vue";
import { TruckStatus } from "@/constants/truck";
import type { AddressDto } from "@/api/geo/dto/address.dto";
import dayjs from "dayjs";
import { useDateTime } from "@/composables";

const { systemDateTimeFormat, getLocalTimeLabel, dateTZ } = useDateTime();

const statusOptions = [TruckStatus.Available, TruckStatus.WillBeAvailable, TruckStatus.NotAvailable];

defineModel<ChangeTruckStatusData | null>({ default: null });

const props = withDefaults(
  defineProps<{
    truck: TruckDto;
    defaultStatus?: TruckStatus | null;
  }>(),
  {
    defaultStatus: null,
  },
);

const emits = defineEmits<{
  (e: "update:modelValue", data: ChangeTruckStatusData): void;
}>();

const status = ref<TruckStatus>(props.defaultStatus || props.truck.status || TruckStatus.Available);
const location = ref<AddressDto | null>(null);
const date = ref<string | null>(null);

const disabledLocation = computed(() => {
  return status.value === TruckStatus.NotAvailable;
});

const showDate = computed(() => {
  return status.value === TruckStatus.WillBeAvailable || (status.value === TruckStatus.NotAvailable && location.value);
});

const requiredDate = computed(() => {
  return status.value === TruckStatus.WillBeAvailable;
});

const minDate = computed(() => {
  if (!location.value?.timezone) {
    return null;
  }

  return requiredDate.value ? dayjs().tz(location.value.timezone).format(systemDateTimeFormat) : null;
});

const timeFieldLabel = computed(() => {
  if (date.value && location.value?.timezone) {
    return getLocalTimeLabel(date.value, location.value.timezone);
  }

  return "";
});

function createAddressFromTruckLocation(truckLocation?: LocationTruckDto): AddressDto | null {
  if (!truckLocation) {
    return null;
  }

  const location: AddressDto = { lat: 0, lon: 0, lng: 0 };

  location.timezone = truckLocation.timezone;
  location.address = {
    city: truckLocation.city,
    state: truckLocation.state,
    zipCode: truckLocation.zipCode,
    country: truckLocation.country,
    fullAddress: truckLocation.fullAddress,
  };
  location.fullAddress = truckLocation.fullAddress || "";

  if (truckLocation.coordinates) {
    location.lat = truckLocation.coordinates.lat;
    location.lon = truckLocation.coordinates.lon;
  }

  return location;
}

function prefillLocation() {
  let actualTruckLocation = props.truck.availableLocation;
  const howHoursSpentFromLastTrack = dayjs().diff(dayjs(props.truck.currentLocationTime), "hours");

  if (!isNaN(howHoursSpentFromLastTrack) && howHoursSpentFromLastTrack < 1 && status.value === TruckStatus.Available) {
    actualTruckLocation = props.truck.currentLocation;
  }

  const locationData = createAddressFromTruckLocation(actualTruckLocation);

  if (locationData?.address?.city && locationData.address.city !== "Unknown") {
    location.value = locationData;
  }
}

function setDefaultDate() {
  if (!props.truck.availableDate || !location.value?.timezone) {
    return;
  }

  date.value = dateTZ(props.truck.availableDate, location.value.timezone, systemDateTimeFormat);
}

function onStatusChange(newStatus: TruckStatus | TruckStatus[] | undefined) {
  if (newStatus === TruckStatus.Available) {
    date.value = "";
  } else if (status.value === TruckStatus.WillBeAvailable) {
    date.value = "";
  }

  prefillLocation();
}

watch(
  [status, location, date],
  ([newStatus, newLocation, newDate]) => {
    const data: ChangeTruckStatusData = {
      status: newStatus,
    };

    if (newLocation && (newStatus === TruckStatus.Available || newStatus === TruckStatus.WillBeAvailable)) {
      data.address = newLocation.fullAddress;
      data.city = newLocation.address?.city;
      data.state = newLocation.address?.state;
      data.zipCode = newLocation.address?.postcode || "";
      data.country = newLocation.address?.country;
      data.lat = newLocation.lat;
      data.lon = newLocation.lon;
    }

    if (showDate.value && newDate) {
      data.availableDate = newDate;
    }

    emits("update:modelValue", data);
  },
  {
    immediate: true,
  },
);

onMounted(() => {
  prefillLocation();
  setDefaultDate();
});
</script>

<template>
  <div>
    <v-row>
      <v-col>
        <truck-status-select
          v-model="status"
          :statuses="statusOptions"
          data-qa="truck-status-change"
          label="Status"
          @update:model-value="onStatusChange"
        />
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <location-picker v-model="location" :disabled="disabledLocation" hide-coordinates label="Location" required />
      </v-col>
    </v-row>

    <div
      v-if="status === TruckStatus.NotAvailable && location"
      class="py-3 font-size-16 font-weight-500 text-grey-darken-2"
    >
      <span class="font-size-12">Not available till:</span>
    </div>

    <date-time-picker
      v-if="showDate"
      v-model="date"
      :min="minDate"
      :required="requiredDate"
      :time-label="timeFieldLabel"
    ></date-time-picker>
  </div>
</template>
